import { useState, useEffect } from 'react';
import {
  Users,
  Shield,
  Plus,
  Pencil,
  Trash2,
  Key,
  Check,
  X,
  Eye,
  EyeOff,
  RotateCcw,
  Building2,
  FileText,
  Layers,
  ScrollText,
  Save,
  Loader2,
} from 'lucide-react';
import PlansPage from './PlansPage';
import { adminSettingsApi, adminApi } from '../../services/api';
import { CGU_SECTIONS, PRIVACY_SECTIONS, type CguSection } from '../../content/cgu-content';
import { useAuthStore } from '../../store/auth.store';

// ============================================================================
// TYPES
// ============================================================================

interface AdminPermission {
  key: string;
  label: string;
}

const LEVEL_LABELS: Record<string, string> = {
  '': 'Aucun droit',
  'view': 'Visualisation',
  'edit': 'Créer et modifier',
  'full': 'Tous les droits',
};

const LEVEL_COLORS: Record<string, string> = {
  '': 'bg-gray-100 text-gray-400',
  'view': 'bg-blue-100 text-blue-700',
  'edit': 'bg-amber-100 text-amber-700',
  'full': 'bg-green-100 text-green-700',
};

/** Extract the level from a permission entry like "admin.tenants:edit" → "edit" */
function getPermLevel(permissions: string[], baseKey: string): string {
  // Check suffixed format first
  for (const level of ['full', 'edit', 'view']) {
    if (permissions.includes(`${baseKey}:${level}`)) return level;
  }
  // Backward compat: bare key = full
  if (permissions.includes(baseKey)) return 'full';
  return '';
}

/** Set permission level for a base key in a permissions array */
function setPermLevel(permissions: string[], baseKey: string, level: string): string[] {
  // Remove any existing entry for this base key
  const filtered = permissions.filter(
    (p) => p !== baseKey && !p.startsWith(baseKey + ':')
  );
  // Add new level (empty = no access)
  if (level) {
    filtered.push(`${baseKey}:${level}`);
  }
  return filtered;
}

interface AdminRole {
  id: string;
  name: string;
  description: string | null;
  permissions: string[];
  isSystem: boolean;
  createdAt: string;
  _count?: { users: number };
}

interface AdminUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  isActive: boolean;
  adminRoleId: string | null;
  notifyHealthCheck: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  adminRole: { id: string; name: string } | null;
}

// ============================================================================
// COMPONENT
// ============================================================================

export default function AdminSettingsPage() {
  const [activeTab, setActiveTab] = useState<'company' | 'users' | 'roles' | 'plans' | 'legal' | 'cgv'>('company');

  const tabs = [
    { id: 'company' as const, label: 'Société', icon: Building2 },
    { id: 'users' as const, label: 'Utilisateurs', icon: Users },
    { id: 'roles' as const, label: 'Rôles & Permissions', icon: Shield },
    { id: 'plans' as const, label: 'Plans', icon: Layers },
    { id: 'legal' as const, label: 'CGU & Confidentialité', icon: ScrollText },
    { id: 'cgv' as const, label: 'CGV Factures', icon: FileText },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Paramètres</h1>
        <p className="text-gray-500 mt-1">Gestion des utilisateurs admin, rôles et informations société</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-gray-100 rounded-xl w-fit">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'company' && <CompanyTab />}
      {activeTab === 'users' && <UsersTab />}
      {activeTab === 'roles' && <RolesTab />}
      {activeTab === 'plans' && <PlansPage />}
      {activeTab === 'legal' && <LegalTab />}
      {activeTab === 'cgv' && <CgvTab />}
    </div>
  );
}

// ============================================================================
// USERS TAB
// ============================================================================

function UsersTab() {
  const currentUser = useAuthStore((s) => s.user);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [roles, setRoles] = useState<AdminRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);
  const [deletingUser, setDeletingUser] = useState<AdminUser | null>(null);
  const [resetUser, setResetUser] = useState<AdminUser | null>(null);
  const [resetPassword, setResetPassword] = useState('');
  const [showResetPwd, setShowResetPwd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    adminRoleId: '' as string | null,
    notifyHealthCheck: true,
  });
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    try {
      const [usersRes, rolesRes] = await Promise.all([
        adminSettingsApi.getUsers(),
        adminSettingsApi.getRoles(),
      ]);
      setUsers(usersRes.data);
      setRoles(rolesRes.data);
    } catch (error) {
      console.error('Failed to load admin users', error);
    } finally {
      setLoading(false);
    }
  }

  function openCreate() {
    setEditingUser(null);
    setFormData({ email: '', password: '', firstName: '', lastName: '', adminRoleId: '', notifyHealthCheck: true });
    setShowModal(true);
  }

  function openEdit(user: AdminUser) {
    setEditingUser(user);
    setFormData({
      email: user.email,
      password: '',
      firstName: user.firstName,
      lastName: user.lastName,
      adminRoleId: user.adminRoleId || '',
      notifyHealthCheck: user.notifyHealthCheck,
    });
    setShowModal(true);
  }

  async function handleSave() {
    setSaving(true);
    try {
      if (editingUser) {
        await adminSettingsApi.updateUser(editingUser.id, {
          firstName: formData.firstName,
          lastName: formData.lastName,
          adminRoleId: formData.adminRoleId || null,
          notifyHealthCheck: formData.notifyHealthCheck,
        });
      } else {
        await adminSettingsApi.createUser({
          email: formData.email,
          password: formData.password,
          firstName: formData.firstName,
          lastName: formData.lastName,
          adminRoleId: formData.adminRoleId || null,
        });
      }
      setShowModal(false);
      await loadData();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Erreur');
    } finally {
      setSaving(false);
    }
  }

  async function handleToggleActive(user: AdminUser) {
    try {
      await adminSettingsApi.updateUser(user.id, { isActive: !user.isActive });
      await loadData();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Erreur');
    }
  }

  async function handleDelete() {
    if (!deletingUser) return;
    try {
      await adminSettingsApi.deleteUser(deletingUser.id);
      setShowDeleteModal(false);
      setDeletingUser(null);
      await loadData();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Erreur');
    }
  }

  async function handleResetPassword() {
    if (!resetUser || !resetPassword) return;
    try {
      await adminSettingsApi.resetPassword(resetUser.id, resetPassword);
      setShowResetModal(false);
      setResetUser(null);
      setResetPassword('');
    } catch (error: any) {
      alert(error.response?.data?.error || 'Erreur');
    }
  }

  if (loading) {
    return (
      <div className="glass-card p-8 text-center">
        <div className="animate-spin h-8 w-8 border-2 border-gray-300 border-t-green-500 rounded-full mx-auto" />
      </div>
    );
  }

  return (
    <>
      <div className="glass-card">
        <div className="p-6 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Utilisateurs administrateurs</h2>
            <p className="text-sm text-gray-500 mt-1">{users.length} utilisateur(s) SUPER_ADMIN</p>
          </div>
          <button onClick={openCreate} className="btn-primary flex items-center gap-2">
            <Plus className="h-4 w-4" />
            Ajouter
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Nom</th>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Email</th>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Rôle</th>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Statut</th>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Dernière connexion</th>
                <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id} className="border-b border-gray-50 hover:bg-gray-50/50">
                  <td className="px-6 py-4">
                    <div className="font-medium text-gray-900">
                      {user.firstName} {user.lastName}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">{user.email}</td>
                  <td className="px-6 py-4">
                    {user.adminRole ? (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                        <Shield className="h-3 w-3" />
                        {user.adminRole.name}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                        <Key className="h-3 w-3" />
                        Accès complet
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <button
                      onClick={() => handleToggleActive(user)}
                      disabled={user.id === currentUser?.id}
                      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                        user.isActive
                          ? 'bg-green-100 text-green-700 hover:bg-green-200'
                          : 'bg-red-100 text-red-700 hover:bg-red-200'
                      } ${user.id === currentUser?.id ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                    >
                      {user.isActive ? (
                        <>
                          <Check className="h-3 w-3" /> Actif
                        </>
                      ) : (
                        <>
                          <X className="h-3 w-3" /> Inactif
                        </>
                      )}
                    </button>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {user.lastLoginAt
                      ? new Date(user.lastLoginAt).toLocaleDateString('fr-FR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })
                      : 'Jamais'}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => openEdit(user)}
                        className="p-2 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                        title="Modifier"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => {
                          setResetUser(user);
                          setResetPassword('');
                          setShowResetPwd(false);
                          setShowResetModal(true);
                        }}
                        className="p-2 rounded-lg text-gray-400 hover:text-orange-600 hover:bg-orange-50 transition-colors"
                        title="Réinitialiser le mot de passe"
                      >
                        <RotateCcw className="h-4 w-4" />
                      </button>
                      {user.id !== currentUser?.id && (
                        <button
                          onClick={() => {
                            setDeletingUser(user);
                            setShowDeleteModal(true);
                          }}
                          className="p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                          title="Supprimer"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create/Edit User Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-xl max-w-md w-full p-6 space-y-5">
            <h3 className="text-lg font-semibold text-gray-900">
              {editingUser ? 'Modifier l\'utilisateur' : 'Nouvel utilisateur admin'}
            </h3>

            {!editingUser && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
                  placeholder="admin@example.com"
                />
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Prénom</label>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
                />
              </div>
            </div>

            {!editingUser && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mot de passe</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
                    placeholder="Min. 8 caractères"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Rôle admin</label>
              <select
                value={formData.adminRoleId || ''}
                onChange={(e) => setFormData({ ...formData, adminRoleId: e.target.value || null })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
              >
                <option value="">Accès complet (aucun rôle)</option>
                {roles.map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.name}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-400 mt-1">
                Sans rôle = accès complet à toutes les sections admin
              </p>
            </div>

            {editingUser && (
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-gray-700">Rapport santé quotidien</p>
                  <p className="text-xs text-gray-400">Recevoir le diagnostic à 7h par email</p>
                </div>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, notifyHealthCheck: !formData.notifyHealthCheck })}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    formData.notifyHealthCheck ? 'bg-green-500' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      formData.notifyHealthCheck ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-2">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-sm text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !formData.firstName || !formData.lastName || (!editingUser && (!formData.email || !formData.password))}
                className="btn-primary"
              >
                {saving ? 'Enregistrement...' : editingUser ? 'Modifier' : 'Créer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && deletingUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowDeleteModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-xl max-w-sm w-full p-6 space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Supprimer l'utilisateur</h3>
            <p className="text-sm text-gray-600">
              Supprimer <strong>{deletingUser.firstName} {deletingUser.lastName}</strong> ({deletingUser.email}) ?
              Cette action est irréversible.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Annuler
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 text-sm text-white bg-red-600 hover:bg-red-700 rounded-lg"
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showResetModal && resetUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowResetModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-xl max-w-sm w-full p-6 space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Réinitialiser le mot de passe</h3>
            <p className="text-sm text-gray-600">
              Nouveau mot de passe pour <strong>{resetUser.firstName} {resetUser.lastName}</strong>
            </p>
            <div className="relative">
              <input
                type={showResetPwd ? 'text' : 'password'}
                value={resetPassword}
                onChange={(e) => setResetPassword(e.target.value)}
                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
                placeholder="Min. 8 caractères"
              />
              <button
                type="button"
                onClick={() => setShowResetPwd(!showResetPwd)}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
              >
                {showResetPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowResetModal(false)}
                className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Annuler
              </button>
              <button
                onClick={handleResetPassword}
                disabled={resetPassword.length < 8}
                className="px-4 py-2 text-sm text-white bg-orange-600 hover:bg-orange-700 rounded-lg disabled:opacity-50"
              >
                Réinitialiser
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ============================================================================
// ROLES TAB
// ============================================================================

function RolesTab() {
  const [roles, setRoles] = useState<AdminRole[]>([]);
  const [permissions, setPermissions] = useState<AdminPermission[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingRole, setEditingRole] = useState<AdminRole | null>(null);
  const [deletingRole, setDeletingRole] = useState<AdminRole | null>(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    permissions: [] as string[],
  });

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    try {
      const [rolesRes, permsRes] = await Promise.all([
        adminSettingsApi.getRoles(),
        adminSettingsApi.getPermissions(),
      ]);
      setRoles(rolesRes.data);
      // Support both old format (array) and new format ({ permissions, levels })
      const permsData = permsRes.data;
      if (Array.isArray(permsData)) {
        setPermissions(permsData);
      } else {
        setPermissions(permsData.permissions || []);
      }
    } catch (error) {
      console.error('Failed to load roles', error);
    } finally {
      setLoading(false);
    }
  }

  function openCreate() {
    setEditingRole(null);
    setFormData({ name: '', description: '', permissions: [] });
    setShowModal(true);
  }

  function openEdit(role: AdminRole) {
    setEditingRole(role);
    setFormData({
      name: role.name,
      description: role.description || '',
      permissions: [...role.permissions],
    });
    setShowModal(true);
  }

  function changePermLevel(baseKey: string, level: string) {
    setFormData((prev) => ({
      ...prev,
      permissions: setPermLevel(prev.permissions, baseKey, level),
    }));
  }

  function selectAll() {
    setFormData((prev) => ({
      ...prev,
      permissions: permissions.map((p) => `${p.key}:full`),
    }));
  }

  function deselectAll() {
    setFormData((prev) => ({ ...prev, permissions: [] }));
  }

  const activePermCount = permissions.filter(
    (p) => getPermLevel(formData.permissions, p.key) !== ''
  ).length;

  async function handleSave() {
    setSaving(true);
    try {
      const data = {
        name: formData.name,
        description: formData.description || null,
        permissions: formData.permissions,
      };
      if (editingRole) {
        await adminSettingsApi.updateRole(editingRole.id, data);
      } else {
        await adminSettingsApi.createRole(data);
      }
      setShowModal(false);
      await loadData();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Erreur');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!deletingRole) return;
    try {
      await adminSettingsApi.deleteRole(deletingRole.id);
      setShowDeleteModal(false);
      setDeletingRole(null);
      await loadData();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Erreur');
    }
  }

  if (loading) {
    return (
      <div className="glass-card p-8 text-center">
        <div className="animate-spin h-8 w-8 border-2 border-gray-300 border-t-green-500 rounded-full mx-auto" />
      </div>
    );
  }

  return (
    <>
      <div className="glass-card">
        <div className="p-6 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Rôles administrateurs</h2>
            <p className="text-sm text-gray-500 mt-1">{roles.length} rôle(s)</p>
          </div>
          <button onClick={openCreate} className="btn-primary flex items-center gap-2">
            <Plus className="h-4 w-4" />
            Nouveau rôle
          </button>
        </div>

        <div className="divide-y divide-gray-100">
          {roles.map((role) => (
            <div key={role.id} className="p-6 flex items-start justify-between hover:bg-gray-50/50 transition-colors">
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <h3 className="font-medium text-gray-900">{role.name}</h3>
                  {role.isSystem && (
                    <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-700">
                      Système
                    </span>
                  )}
                  <span className="text-xs text-gray-400">
                    {role._count?.users ?? 0} utilisateur(s)
                  </span>
                </div>
                {role.description && (
                  <p className="text-sm text-gray-500 mt-1">{role.description}</p>
                )}
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {permissions.map((perm) => {
                    const level = getPermLevel(role.permissions, perm.key);
                    if (!level) return null;
                    return (
                      <span
                        key={perm.key}
                        className={`px-2 py-0.5 text-xs rounded-full ${LEVEL_COLORS[level]}`}
                      >
                        {perm.label} — {LEVEL_LABELS[level]}
                      </span>
                    );
                  })}
                  {role.permissions.length === 0 && (
                    <span className="text-xs text-gray-400 italic">Aucune permission</span>
                  )}
                </div>
              </div>
              {!role.isSystem && (
                <div className="flex items-center gap-1 ml-4">
                  <button
                    onClick={() => openEdit(role)}
                    className="p-2 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                    title="Modifier"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => {
                      setDeletingRole(role);
                      setShowDeleteModal(true);
                    }}
                    className="p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                    title="Supprimer"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
          ))}
          {roles.length === 0 && (
            <div className="p-8 text-center text-gray-400">Aucun rôle défini</div>
          )}
        </div>
      </div>

      {/* Create/Edit Role Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-xl max-w-lg w-full p-6 space-y-5 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900">
              {editingRole ? 'Modifier le rôle' : 'Nouveau rôle'}
            </h3>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nom du rôle</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
                placeholder="Ex: Support, Comptabilité..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <input
                type="text"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
                placeholder="Description optionnelle"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-medium text-gray-700">Permissions</label>
                <div className="flex gap-2">
                  <button
                    onClick={selectAll}
                    className="text-xs text-green-600 hover:text-green-700 font-medium"
                  >
                    Tous les droits
                  </button>
                  <span className="text-gray-300">|</span>
                  <button
                    onClick={deselectAll}
                    className="text-xs text-gray-500 hover:text-gray-700 font-medium"
                  >
                    Aucun droit
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                {permissions.map((perm) => {
                  const currentLevel = getPermLevel(formData.permissions, perm.key);
                  return (
                    <div
                      key={perm.key}
                      className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                        currentLevel
                          ? 'border-green-200 bg-green-50/50'
                          : 'border-gray-200'
                      }`}
                    >
                      <span className="text-sm font-medium text-gray-700">{perm.label}</span>
                      <select
                        value={currentLevel}
                        onChange={(e) => changePermLevel(perm.key, e.target.value)}
                        className={`text-sm rounded-lg border px-3 py-1.5 focus:ring-2 focus:ring-green-500/20 focus:border-green-500 ${
                          currentLevel === 'full' ? 'bg-green-50 border-green-300 text-green-700' :
                          currentLevel === 'edit' ? 'bg-amber-50 border-amber-300 text-amber-700' :
                          currentLevel === 'view' ? 'bg-blue-50 border-blue-300 text-blue-700' :
                          'bg-white border-gray-300 text-gray-500'
                        }`}
                      >
                        <option value="">Aucun droit</option>
                        <option value="view">Visualisation</option>
                        <option value="edit">Créer et modifier</option>
                        <option value="full">Tous les droits</option>
                      </select>
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-gray-400 mt-2">
                {activePermCount}/{permissions.length} pages autorisées
              </p>
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-sm text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !formData.name}
                className="btn-primary"
              >
                {saving ? 'Enregistrement...' : editingRole ? 'Modifier' : 'Créer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && deletingRole && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowDeleteModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-xl max-w-sm w-full p-6 space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Supprimer le rôle</h3>
            <p className="text-sm text-gray-600">
              Supprimer le rôle <strong>{deletingRole.name}</strong> ?
              {(deletingRole._count?.users ?? 0) > 0 && (
                <span className="block mt-2 text-red-600">
                  Ce rôle est assigné à {deletingRole._count?.users} utilisateur(s).
                  Veuillez les réassigner d'abord.
                </span>
              )}
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Annuler
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 text-sm text-white bg-red-600 hover:bg-red-700 rounded-lg"
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ============================================================================
// COMPANY TAB
// ============================================================================

interface CompanySettings {
  companyName: string;
  companyTagline?: string;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  supportEmail?: string;
  legalName?: string;
  address?: string;
  city?: string;
  postalCode?: string;
  country?: string;
  phone?: string;
  email?: string;
  website?: string;
  vatNumber?: string;
  siret?: string;
  showRegistration?: boolean;
  iban?: string;
  bic?: string;
  rcs?: string;
  capitalSocial?: string;
  formeJuridique?: string;
}

function CompanyTab() {
  const [settings, setSettings] = useState<CompanySettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadSettings();
  }, []);

  async function loadSettings() {
    try {
      setLoading(true);
      const data = await adminApi.getAppearanceSettings();
      setSettings(data);
    } catch {
      setError('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }

  async function handleSave() {
    if (!settings) return;
    try {
      setSaving(true);
      setError(null);
      await adminApi.updateAppearanceSettings(settings);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      setError('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  }

  function updateField(field: keyof CompanySettings, value: string) {
    setSettings((prev) => prev ? { ...prev, [field]: value } : prev);
  }

  if (loading) {
    return (
      <div className="glass-card p-8 text-center">
        <div className="animate-spin h-8 w-8 border-2 border-gray-300 border-t-green-500 rounded-full mx-auto" />
      </div>
    );
  }

  if (!settings) return null;

  const inputClass = 'w-full px-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500 text-sm';

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <span className="text-red-700 text-sm">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto text-red-500 hover:text-red-700">&times;</button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Informations légales */}
        <div className="glass-card p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Informations légales</h3>
          <p className="text-sm text-gray-500 mb-4">Ces informations apparaissent sur les factures</p>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Raison sociale</label>
              <input type="text" value={settings.legalName || ''} onChange={(e) => updateField('legalName', e.target.value)} className={inputClass} placeholder="TrackFleet S.à r.l." />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Forme juridique</label>
              <input type="text" value={settings.formeJuridique || ''} onChange={(e) => updateField('formeJuridique', e.target.value)} className={inputClass} placeholder="Société à responsabilité limitée" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">N° SIRET</label>
                <input type="text" value={settings.siret || ''} onChange={(e) => updateField('siret', e.target.value)} className={inputClass} placeholder="123 456 789 00012" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">N° TVA</label>
                <input type="text" value={settings.vatNumber || ''} onChange={(e) => updateField('vatNumber', e.target.value)} className={inputClass} placeholder="LU12345678" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">RCS</label>
                <input type="text" value={settings.rcs || ''} onChange={(e) => updateField('rcs', e.target.value)} className={inputClass} placeholder="Luxembourg B123456" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Capital social</label>
                <input type="text" value={settings.capitalSocial || ''} onChange={(e) => updateField('capitalSocial', e.target.value)} className={inputClass} placeholder="12 000 €" />
              </div>
            </div>
          </div>
        </div>

        {/* Adresse */}
        <div className="glass-card p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Adresse & Site web</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Adresse</label>
              <input type="text" value={settings.address || ''} onChange={(e) => updateField('address', e.target.value)} className={inputClass} placeholder="2 Rue de Bitbourg" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Code postal</label>
                <input type="text" value={settings.postalCode || ''} onChange={(e) => updateField('postalCode', e.target.value)} className={inputClass} placeholder="L-1273" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ville</label>
                <input type="text" value={settings.city || ''} onChange={(e) => updateField('city', e.target.value)} className={inputClass} placeholder="Luxembourg" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Pays</label>
                <input type="text" value={settings.country || ''} onChange={(e) => updateField('country', e.target.value)} className={inputClass} placeholder="Luxembourg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Site web</label>
                <input type="url" value={settings.website || ''} onChange={(e) => updateField('website', e.target.value)} className={inputClass} placeholder="https://trackfleet.lu" />
              </div>
            </div>
          </div>
        </div>

        {/* Coordonnées bancaires */}
        <div className="glass-card p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Coordonnées bancaires</h3>
          <p className="text-sm text-gray-500 mb-3">Affichées en pied de facture</p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">IBAN</label>
              <input type="text" value={settings.iban || ''} onChange={(e) => updateField('iban', e.target.value)} className={inputClass} placeholder="LU28 0019 4006 4475 0000" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">BIC</label>
              <input type="text" value={settings.bic || ''} onChange={(e) => updateField('bic', e.target.value)} className={inputClass} placeholder="BCEELULL" />
            </div>
          </div>
        </div>

        {/* Contact */}
        <div className="glass-card p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Contact</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
              <input type="text" value={settings.phone || ''} onChange={(e) => updateField('phone', e.target.value)} className={inputClass} placeholder="+352 621 123 456" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input type="email" value={settings.email || ''} onChange={(e) => updateField('email', e.target.value)} className={inputClass} placeholder="contact@trackfleet.lu" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email de support</label>
              <input type="email" value={settings.supportEmail || ''} onChange={(e) => updateField('supportEmail', e.target.value)} className={inputClass} placeholder="support@trackfleet.lu" />
            </div>
          </div>
        </div>
      </div>

      {/* Save button */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className={`flex items-center gap-2 px-6 py-2.5 rounded-lg font-medium transition-colors ${
            saved
              ? 'bg-green-100 text-green-700'
              : 'bg-green-600 text-white hover:bg-green-700'
          }`}
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : saved ? <Check className="h-4 w-4" /> : <Save className="h-4 w-4" />}
          {saved ? 'Enregistré !' : 'Enregistrer'}
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// LEGAL TAB (CGU & Privacy)
// ============================================================================

function serializeSections(sections: CguSection[]): string {
  return sections.map((s) => `${s.title}\n${s.content}`).join('\n\n');
}

function LegalTab() {
  const [cguText, setCguText] = useState('');
  const [privacyText, setPrivacyText] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadTexts();
  }, []);

  async function loadTexts() {
    try {
      setLoading(true);
      const data = await adminApi.getLegalTexts();
      setCguText(data.cguText || serializeSections(CGU_SECTIONS));
      setPrivacyText(data.privacyText || serializeSections(PRIVACY_SECTIONS));
    } catch {
      // Fallback to hardcoded content
      setCguText(serializeSections(CGU_SECTIONS));
      setPrivacyText(serializeSections(PRIVACY_SECTIONS));
    } finally {
      setLoading(false);
    }
  }

  async function handleSave() {
    try {
      setSaving(true);
      setError(null);
      await adminApi.updateLegalTexts(cguText, privacyText);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      setError('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="glass-card p-8 text-center">
        <div className="animate-spin h-8 w-8 border-2 border-gray-300 border-t-green-500 rounded-full mx-auto" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <span className="text-red-700 text-sm">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto text-red-500 hover:text-red-700">&times;</button>
        </div>
      )}

      <div className="glass-card p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Conditions Générales d'Utilisation (CGU)</h3>
        <p className="text-sm text-gray-500 mb-4">
          Ce texte est présenté aux utilisateurs lors de leur première connexion. Le format attendu est : titre de section sur une ligne, contenu en dessous, sections séparées par une ligne vide.
        </p>
        <textarea
          value={cguText}
          onChange={(e) => setCguText(e.target.value)}
          className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500 text-sm font-mono"
          rows={16}
          placeholder="1. Introduction&#10;Les présentes conditions..."
        />
      </div>

      <div className="glass-card p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Politique de Confidentialité</h3>
        <p className="text-sm text-gray-500 mb-4">
          Ce texte est présenté en dessous des CGU lors de la première connexion.
        </p>
        <textarea
          value={privacyText}
          onChange={(e) => setPrivacyText(e.target.value)}
          className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500 text-sm font-mono"
          rows={16}
          placeholder="1. Collecte et traitement des données personnelles&#10;..."
        />
      </div>

      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className={`flex items-center gap-2 px-6 py-2.5 rounded-lg font-medium transition-colors ${
            saved
              ? 'bg-green-100 text-green-700'
              : 'bg-green-600 text-white hover:bg-green-700'
          }`}
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : saved ? <Check className="h-4 w-4" /> : <Save className="h-4 w-4" />}
          {saved ? 'Enregistré !' : 'Enregistrer'}
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// CGV TAB
// ============================================================================

function CgvTab() {
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadCgv();
  }, []);

  async function loadCgv() {
    try {
      setLoading(true);
      const data = await adminApi.getInvoiceCgv();
      setText(data.text || '');
    } catch {
      setError('Erreur lors du chargement des CGV');
    } finally {
      setLoading(false);
    }
  }

  async function handleSave() {
    try {
      setSaving(true);
      setError(null);
      await adminApi.updateInvoiceCgv(text);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      setError('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="glass-card p-8 text-center">
        <div className="animate-spin h-8 w-8 border-2 border-gray-300 border-t-green-500 rounded-full mx-auto" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <span className="text-red-700 text-sm">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto text-red-500 hover:text-red-700">&times;</button>
        </div>
      )}

      <div className="glass-card p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Conditions Générales de Vente</h3>
        <p className="text-sm text-gray-500 mb-4">
          Ce texte sera ajouté en page 2 de chaque facture PDF générée. Laissez vide pour ne pas inclure de CGV.
        </p>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500 text-sm font-mono"
          rows={20}
          style={{ minHeight: '400px' }}
          placeholder="Article 1 - Objet&#10;Les présentes conditions générales de vente régissent...&#10;&#10;Article 2 - Prix&#10;..."
        />
      </div>

      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className={`flex items-center gap-2 px-6 py-2.5 rounded-lg font-medium transition-colors ${
            saved
              ? 'bg-green-100 text-green-700'
              : 'bg-green-600 text-white hover:bg-green-700'
          }`}
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : saved ? <Check className="h-4 w-4" /> : <Save className="h-4 w-4" />}
          {saved ? 'Enregistré !' : 'Enregistrer'}
        </button>
      </div>
    </div>
  );
}
